import React, { useCallback, useEffect, useState } from 'react';
import type { ClientDto, GuardAnalysisDto, GuardDeviceDto, GuardEventDto, GuardSeverity, GuardSiteDetailDto, GuardSiteDto, GuardUsbMode } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { useNavState } from '../navState';
import { Icon } from '../icons';
import { Empty, ErrorBanner, Field, Modal, Panel, Pill, Stat, StatusDot } from '../ui';
import { ask } from '../dialogs';
import { saveBlob } from '../files';
import { SecurityPlan, type Workspace } from './SecurityPlan';
import { SecurityOverview } from './SecurityOverview';
import { SecurityAcn } from './SecurityAcn';
import { SecuritySiem } from './SecuritySiem';
import { SecurityAntivirus } from './SecurityAntivirus';
import { SecurityTesting } from './SecurityTesting';
import { SecurityAssets } from './SecurityAssets';
import { SecurityTraining } from './SecurityTraining';
import { SecurityIncidents } from './SecurityIncidents';
import { SecurityDocuments } from './SecurityDocuments';
import { SecurityMail } from './SecurityMail';
import { SecuritySonda } from './SecuritySonda';

// Tecnoside Guard: i PC dei clienti con l'agente installato, le allerte (antivirus spento, minacce, chiavette)
// e l'analisi dell'AI per il titolare. Solo per il consulente (admin).

const SEV_LABEL: Record<GuardSeverity, string> = { critical: 'Critica', high: 'Alta', medium: 'Media', low: 'Bassa', info: 'Info' };
const SEV_TONE: Record<GuardSeverity, 'bad' | 'warn' | 'info' | 'soft'> = { critical: 'bad', high: 'bad', medium: 'warn', low: 'soft', info: 'soft' };
const RISK_TONE: Record<GuardAnalysisDto['risk'], 'good' | 'warn' | 'bad'> = { basso: 'good', medio: 'warn', alto: 'bad', critico: 'bad' };
const workspaceTabs: { id: Workspace; label: string }[] = [
  { id: 'overview', label: 'Panoramica' },
  { id: 'guide', label: 'Guida' },
  { id: 'acn', label: 'Misure ACN' },
  { id: 'assets', label: 'Inventario' },
  { id: 'testing', label: 'Verifiche' },
  { id: 'siem', label: 'SIEM' },
  { id: 'training', label: 'Formazione' },
  { id: 'incidents', label: 'Incidenti' },
  { id: 'mail', label: 'Email' },
  { id: 'sonda', label: 'Sonda' },
  { id: 'documents', label: 'Documenti NIS2' },
  { id: 'devices', label: 'PC e avvisi' },
  { id: 'antivirus', label: 'Antivirus' },
  { id: 'settings', label: 'Impostazioni' },
];

const when = (iso: string | null) => {
  if (!iso) return 'mai';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'ora';
  if (min < 60) return `${min} min fa`;
  if (min < 24 * 60) return `${Math.round(min / 60)} h fa`;
  return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
};
const dateTime = (iso: string) => new Date(iso).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const isAlert = (e: GuardEventDto) => !e.ackAt && ['medium', 'high', 'critical'].includes(e.severity);

export function SecurityPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [sites, setSites] = useState<GuardSiteDto[] | null>(null);
  const [ai, setAi] = useState(false);
  const [openId, setOpenId] = useNavState<number | null>(
    ctx, 'site', typeof ctx.pageParam === 'number' && Number.isSafeInteger(ctx.pageParam) && ctx.pageParam > 0 ? ctx.pageParam : null,
  );
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState<{ site: string; code: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ sites: GuardSiteDto[]; ai: boolean }>('/api/guard/sites');
      setSites(r.sites);
      setAi(r.ai);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  // Allerta in tempo reale da un PC: aggiorno i numeri
  useEffect(() => {
    if (ctx.event?.type === 'app.notice' && ctx.event.title.startsWith('Sicurezza')) void load();
  }, [ctx.eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  if (openId) {
    return <SiteDetail key={openId} ctx={ctx} siteId={openId} ai={ai} onBack={() => { setOpenId(null); void load(); }} onCode={setCode} codeModal={code} closeCode={() => setCode(null)} />;
  }

  const alerts = (sites ?? []).reduce((n, s) => n + s.openAlerts, 0);
  return (
    <div className="panels single">
      <Panel
        title="Sicurezza clienti"
        subtitle={
          sites
            ? `Monitoraggio continuo con Guard: ${sites.length === 1 ? '1 azienda' : `${sites.length} aziende`} · ${sites.reduce((n, s) => n + s.devices, 0)} PC registrati · ${sites.reduce((n, s) => n + s.online, 0)} in linea${alerts ? ` · ${alerts === 1 ? '1 avviso' : `${alerts} avvisi`} da vedere` : ''}`
            : undefined
        }
        right={
          <button type="button" className="btn dark" title="Crea il codice per installare Tecnoside Guard sui PC di un’azienda" onClick={() => setCreating(true)}>
            <Icon name="plus" size={16} /> Proteggi un’azienda
          </button>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {!sites ? (
          <div className="muted small">Caricamento…</div>
        ) : sites.length === 0 ? (
          <Empty icon="shield">Nessun cliente protetto: premi «Proteggi un’azienda» per avere il codice.</Empty>
        ) : (
          <div className="ag-list">
            {sites.map((s) => (
              <div key={s.id} className="ag-row" onClick={() => ctx.setSub({ site: s.id, ws: 'overview' })}>
                <Icon name="shield" size={18} />
                <span className="ag-title">
                  <b>{s.name}</b>
                  <div className="muted small">
                    {s.inventoryPcs > 0 ? `${s.inventoryPcs} PC nell’inventario · ` : ''}{s.devices} PC registrati · {s.online} in linea{s.clientName ? ` · ${s.clientName}` : ''}
                    {s.analysis ? ` · rischio ${s.analysis.risk}` : ''}
                  </div>
                </span>
                {s.openAlerts > 0 ? (
                  <Pill tone={SEV_TONE[s.worst ?? 'medium']} title="Avvisi arrivati dai PC e non ancora guardati">{s.openAlerts} da vedere</Pill>
                ) : s.devices > 0 && s.inventoryPcs > s.devices ? (
                  <Pill tone="warn" title="PC censiti dai report Assessment e PC che inviano dati con Guard. I due elenchi vanno ancora confrontati.">
                    {s.devices} PC registrati · {s.inventoryPcs} nell’inventario
                  </Pill>
                ) : s.devices > 0 && s.online < s.devices ? (
                  <Pill tone="warn">{s.online} di {s.devices} PC registrati in linea</Pill>
                ) : s.devices > 0 ? (
                  <Pill tone="good">{s.devices === 1 ? '1 PC invia dati con Guard' : `${s.devices} PC inviano dati con Guard`}</Pill>
                ) : (
                  <Pill tone="soft" title="I PC nell’inventario provengono dai report Assessment. Guard non è ancora collegato su questi PC.">
                    {s.inventoryPcs > 1 ? `Nessuno dei ${s.inventoryPcs} PC invia dati con Guard` : s.inventoryPcs === 1 ? 'Il PC nell’inventario non invia dati con Guard' : 'Nessun PC invia dati con Guard'}
                  </Pill>
                )}
                <button type="button" className="btn small" title={`Apri i registri di sicurezza di ${s.name}`} onClick={(e) => { e.stopPropagation(); ctx.setSub({ site: s.id, ws: 'siem' }); }}>
                  Apri SIEM
                </button>
              </div>
            ))}
          </div>
        )}
      </Panel>
      {creating && (
        <NewSiteModal
          ctx={ctx}
          onClose={() => setCreating(false)}
          onCreated={(site, c) => {
            setCreating(false);
            setCode({ site: site.name, code: c });
            void load();
          }}
        />
      )}
      {code && <CodeModal ctx={ctx} site={code.site} code={code.code} onClose={() => setCode(null)} />}
    </div>
  );
}

export function NewSiteModal({ ctx, onClose, onCreated }: { ctx: Ctx; onClose: () => void; onCreated: (s: GuardSiteDto, code: string) => void }) {
  const [deviceLimit, setDeviceLimit] = useState('5');
  const [trialDays, setTrialDays] = useState('14');
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [clientId, setClientId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void ctx.api.get<ClientDto[]>('/api/admin/clients').then(setClients).catch((e) => setError((e as Error).message));
  }, [ctx.api]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await ctx.api.post<{ site: GuardSiteDto; code: string }>('/api/guard/sites', {
        clientId, deviceLimit: Number(deviceLimit), trialDays: Number(trialDays),
      });
      onCreated(r.site, r.code);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Proteggi un’azienda"
      narrow
      onClose={onClose}
      footer={
        <button type="button" className="btn dark" disabled={busy || !clientId || !Number.isInteger(Number(deviceLimit)) || Number(deviceLimit) < 1 || !Number.isInteger(Number(trialDays)) || Number(trialDays) < 1 || Number(trialDays) > 30} onClick={() => void save()}>
          Crea la prova gratuita
        </button>
      }
    >
      <ErrorBanner message={error} />
      <Field label="Cliente nel CRM" hint="Collega i PC all'azienda giusta, se è già in elenco">
        <select value={clientId ?? ''} onChange={(e) => {
          const id = e.target.value ? Number(e.target.value) : null;
          setClientId(id);
        }}>
          <option value="">Scegli un cliente del CRM</option>
          {clients.filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Quanti PC vuoi controllare?" hint="La prova gratuita collega fino a questo numero di PC.">
        <input type="number" min="1" max="100000" value={deviceLimit} onChange={(e) => setDeviceLimit(e.target.value)} />
      </Field>
      <Field label="Giorni di prova" hint="Da 1 a 30 giorni. Partono dal primo PC attivato.">
        <input type="number" min="1" max="30" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} />
      </Field>
    </Modal>
  );
}

export function CodeModal({ ctx, site, code, onClose }: { ctx: Ctx; site: string; code: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const command = `TecnosideGuard.exe install --server ${ctx.api.baseUrl} --code ${code}`;
  const download = async () => {
    setError(null);
    setDownloading(true);
    try { saveBlob(await ctx.api.download('/api/guard/installer'), 'Setup Sentinel.exe'); }
    catch (e) { setError((e as Error).message); }
    finally { setDownloading(false); }
  };
  return (
    <Modal title={`Codice per ${site}`} narrow onClose={onClose}>
      <ErrorBanner message={error} />
      <p className="muted small" style={{ marginTop: 0 }} title="Se lo perdi, ne crei uno nuovo dalla scheda dell’azienda.">
        Si vede <b>solo adesso</b>: copialo. Vale per tutti i PC dell’azienda.
      </p>
      <div className="guard-code">{code}</div>
      <p className="small">Incolla il codice in Sentinel e premi «Attiva». I giorni di prova partono dal primo PC attivato.</p>
      <button type="button" className="btn dark" disabled={downloading} onClick={() => void download()}>{downloading ? 'Scarico…' : 'Scarica il file di installazione'}</button>
      <p className="small muted">Lo stesso codice vale per tutti i PC dell’azienda.</p>
      <details><summary className="small">Installazione per tecnici</summary>
      <p className="small" style={{ marginBottom: 6 }}>
        Su ogni PC, da una finestra di amministratore, nella cartella di Tecnoside Guard:
      </p>
      <pre className="guard-cmd">{command}</pre>
      <button
        type="button"
        className="btn"
        onClick={() => {
          void navigator.clipboard.writeText(command).then(() => setCopied(true));
        }}
      >
        <Icon name={copied ? 'check' : 'copy'} size={16} /> {copied ? 'Copiato' : 'Copia il comando'}
      </button>
      </details>
    </Modal>
  );
}

function SiteDetail({
  ctx,
  siteId,
  ai,
  onBack,
  onCode,
  codeModal,
  closeCode,
}: {
  ctx: Ctx;
  siteId: number;
  ai: boolean;
  onBack: () => void;
  onCode: (c: { site: string; code: string }) => void;
  codeModal: { site: string; code: string } | null;
  closeCode: () => void;
}) {
  const { api } = ctx;
  const [site, setSite] = useState<GuardSiteDetailDto | null>(null);
  const [device, setDevice] = useState<GuardDeviceDto | null>(null);
  const [allEvents, setAllEvents] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspace, setWorkspace] = useNavState<Workspace>(ctx, 'ws', 'overview');

  const load = useCallback(async () => {
    try {
      setSite(await api.get<GuardSiteDetailDto>(`/api/guard/sites/${siteId}`));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, siteId]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (ctx.event?.type === 'app.notice' && ctx.event.title.startsWith('Sicurezza')) void load();
  }, [ctx.eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  const ack = async (ids: number[]) => {
    try {
      await api.post('/api/guard/events/ack', { ids });
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      await api.post(`/api/guard/sites/${siteId}/analyze`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAnalyzing(false);
    }
  };

  const renew = async () => {
    if (!site || !(await ask('Creare un nuovo codice? Quello vecchio smette di funzionare (i PC già installati restano collegati).'))) return;
    try {
      const r = await api.post<{ code: string }>(`/api/guard/sites/${siteId}/code`);
      onCode({ site: site.name, code: r.code });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async () => {
    if (!site || !(await ask(`Eliminare «${site.name}» dalla sicurezza? Si cancellano PC, avvisi e verifiche; i PC smettono di mandare dati.`))) return;
    try {
      await api.del(`/api/guard/sites/${siteId}`);
      onBack();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (!site) {
    return (
      <div className="panels single">
        <Panel title="Sicurezza">
          <ErrorBanner message={error} />
          <div className="muted small">Caricamento…</div>
        </Panel>
      </div>
    );
  }

  const open = site.events.filter(isAlert);
  const shown = allEvents ? site.events : open;
  const toFix = site.deviceList.reduce((n, d) => n + (d.report?.nonCompliant ?? 0), 0);

  return (
    <div className="panels single">
      <Panel
        title={
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn icon ghost" title="Torna all’elenco delle aziende" onClick={onBack}>
              <Icon name="chevronLeft" size={18} />
            </button>
            {site.name}
          </span>
        }
        subtitle={`Codice per installare: …${site.codeHint ?? '----'}`}
        right={
          <div className="row-actions" style={{ margin: 0 }}>
            <button type="button" className="btn" title="Serve se il codice è andato perso: quello vecchio smette di funzionare" onClick={() => void renew()}>
              Nuovo codice
            </button>
            <button type="button" className="btn ghost" title="Togli questa azienda dalla sicurezza" onClick={() => void remove()}>
              <Icon name="trash" size={16} />
            </button>
          </div>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <div role="tablist" aria-label="Lavoro per questa azienda" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, borderBottom: '1px solid var(--line)', paddingBottom: 10, marginBottom: 8 }}>
          {workspaceTabs.map((tab) => <button key={tab.id} type="button" role="tab" aria-selected={workspace === tab.id} className={`btn small ${workspace === tab.id ? 'dark' : 'ghost'}`} onClick={() => setWorkspace(tab.id)}>{tab.label}</button>)}
        </div>
        {workspace !== 'overview' && <>
        <div className="stats" style={{ marginBottom: 16 }}>
          <Stat value={site.devices} label="PC registrati" />
          <Stat value={site.online} label="In linea ora" />
          <Stat value={open.length} label="Avvisi da vedere" navy={open.length > 0} />
          <Stat value={toFix} label="Problemi segnalati da Guard" />
        </div>
        <p className="muted small" style={{ margin: '0 0 12px' }}>Questi numeri riguardano Guard. I PC già controllati con Assessment sono nell’Inventario.</p>

        </>}
        {workspace === 'overview' && <SecurityOverview ctx={ctx} site={site} onNavigate={setWorkspace} />}
        {workspace === 'acn' && <SecurityAcn ctx={ctx} siteId={siteId} />}
        {workspace === 'guide' && <SecurityPlan ctx={ctx} siteId={siteId} siteName={site.name} onNavigate={setWorkspace} />}
        {workspace === 'assets' && <SecurityAssets ctx={ctx} siteId={siteId} />}
        {workspace === 'testing' && <SecurityTesting ctx={ctx} siteId={siteId} />}
        {workspace === 'siem' && <SecuritySiem ctx={ctx} siteId={siteId} active={site.modules.siem} onChanged={load} />}
        {workspace === 'settings' && <SiteSettings ctx={ctx} site={site} onSaved={load} />}
        {workspace === 'training' && <SecurityTraining ctx={ctx} siteId={siteId} />}
        {workspace === 'incidents' && <SecurityIncidents ctx={ctx} siteId={siteId} />}
        {workspace === 'documents' && <SecurityDocuments ctx={ctx} siteId={siteId} />}
        {workspace === 'mail' && <SecurityMail ctx={ctx} siteId={siteId} />}
        {workspace === 'sonda' && <SecuritySonda ctx={ctx} siteId={siteId} />}
        {workspace === 'antivirus' && <SecurityAntivirus ctx={ctx} site={site} onChanged={load} />}
        {workspace === 'devices' && <>
        <AnalysisBox analysis={site.analysis} ai={ai} busy={analyzing} hasDevices={site.devices > 0} onAnalyze={() => void analyze()} />

        <h3 className="guard-h">PC registrati con Guard</h3>
        {site.deviceList.length === 0 ? (
          <Empty icon="server">Nessun PC collegato a Guard. I PC già controllati con Assessment sono nell’Inventario.</Empty>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>PC</th>
                <th>Antivirus</th>
                <th>Firewall</th>
                <th title="BitLocker: in caso di furto del PC i dati restano illeggibili">Cifratura del disco</th>
                <th>Aggiornamenti</th>
                <th title="La verifica completa del PC, una volta al giorno">Verifica</th>
                <th title="Regole sulle chiavette USB applicate da Guard">Chiavette</th>
                <th className="num">Avvisi</th>
              </tr>
            </thead>
            <tbody>
              {site.deviceList.map((d) => (
                <DeviceRow key={d.id} d={d} usb={site.modules.usb ?? 'spento'} onOpen={() => setDevice(d)} />
              ))}
            </tbody>
          </table>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20 }}>
          <h3 className="guard-h grow" style={{ margin: 0 }}>
            {allEvents ? 'Tutto quello che è successo' : 'Avvisi da vedere'}
          </h3>
          {!allEvents && open.length > 1 && (
            <button type="button" className="btn small" title="Li hai guardati tutti: spariscono da qui" onClick={() => void ack(open.map((e) => e.id))}>
              Segna tutti come visti
            </button>
          )}
          <button type="button" className="btn small ghost" onClick={() => setAllEvents((v) => !v)}>
            {allEvents ? 'Solo gli avvisi' : 'Tutto quello che è successo'}
          </button>
        </div>
        {shown.length === 0 ? (
          <div className="muted small" style={{ padding: '10px 0' }}>
            {allEvents ? 'Niente da mostrare.' : 'Nessun avviso da vedere.'}
          </div>
        ) : (
          <div className="ag-list">
            {shown.slice(0, allEvents ? 200 : 50).map((e) => (
              <EventRow key={e.id} e={e} onAck={isAlert(e) ? () => void ack([e.id]) : undefined} />
            ))}
          </div>
        )}
        </>}
      </Panel>
      {device && <DeviceModal d={device} usb={site.modules.usb ?? 'spento'} events={site.events.filter((e) => e.deviceId === device.id)} onClose={() => setDevice(null)}
        onLabel={async (label, location) => {
          try {
            await api.put(`/api/guard/devices/${device.id}/label`, { label, location });
            setDevice(null);
            await load();
          } catch (err) {
            setError((err as Error).message);
          }
        }}
        onUsbException={async (note) => {
          try {
            await api.put(`/api/guard/devices/${device.id}/usb-exception`, { note });
            setDevice(null);
            await load();
          } catch (err) {
            setError((err as Error).message);
          }
        }}
        onDelete={async () => {
        if (!(await ask(`Liberare il posto di «${device.name}»? Il programma smette di inviare dati. Report e avvisi già ricevuti restano conservati.`))) return;
        try {
          await api.del(`/api/guard/devices/${device.id}`);
          setDevice(null);
          await load();
        } catch (err) {
          setError((err as Error).message);
        }
      }} />}
      {codeModal && <CodeModal ctx={ctx} site={codeModal.site} code={codeModal.code} onClose={closeCode} />}
    </div>
  );
}

function SiteSettings({ ctx, site, onSaved }: { ctx: Ctx; site: GuardSiteDetailDto; onSaved: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [modules, setModules] = useState(site.modules);
  const [licenseKind, setLicenseKind] = useState(site.licenseKind);
  const [updateChannel, setUpdateChannel] = useState(site.updateChannel);
  const [expiresOn, setExpiresOn] = useState(site.licenseExpiresOn ?? '');
  const [limit, setLimit] = useState(site.deviceLimit?.toString() ?? '');
  const [siemExpected, setSiemExpected] = useState(site.siemExpectedAgents?.toString() ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setYears = (years: number) => {
    const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    const date = new Date(`${today}T12:00:00Z`);
    date.setUTCFullYear(date.getUTCFullYear() + years);
    setExpiresOn(date.toISOString().slice(0, 10));
  };

  useEffect(() => {
    if (editing) return;
    setModules(site.modules);
    setLicenseKind(site.licenseKind);
    setUpdateChannel(site.updateChannel);
    setExpiresOn(site.licenseExpiresOn ?? '');
    setLimit(site.deviceLimit?.toString() ?? '');
    setSiemExpected(site.siemExpectedAgents?.toString() ?? '');
  }, [site, editing]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await ctx.api.put(`/api/guard/sites/${site.id}/settings`, {
        modules,
        licenseKind,
        updateChannel,
        licenseExpiresOn: expiresOn || null,
        deviceLimit: limit ? Number(limit) : null,
        siemExpectedAgents: siemExpected ? Number(siemExpected) : null,
      });
      setEditing(false);
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const expired = !!site.licenseExpiresOn && site.licenseExpiresOn < new Intl.DateTimeFormat('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h3 className="guard-h grow" style={{ margin: 0 }}>Servizi attivi</h3>
        <Pill tone={expired ? 'bad' : site.deviceLimit ? 'good' : 'warn'}>{expired ? 'Scaduto' : site.licenseKind === 'trial' ? 'Prova gratuita' : site.deviceLimit ? 'Licenza attiva' : 'Posti da impostare'}</Pill>
        <button type="button" className="btn small" onClick={() => setEditing((v) => !v)}>{editing ? 'Annulla' : 'Modifica'}</button>
      </div>
      <p className="muted small" style={{ margin: '8px 0 12px' }}>Scegli cosa può usare questa azienda. I servizi spenti non inviano dati.</p>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {editing ? (
        <>
          <Field label="Tipo di licenza" hint="Dopo l'acquisto scegli Attiva: i PC restano collegati, senza reinstallare.">
            <select value={licenseKind} onChange={(e) => setLicenseKind(e.target.value as 'trial' | 'paid')}>
              <option value="trial">Prova gratuita</option>
              <option value="paid">Attiva</option>
            </select>
          </Field>
          <Field label="Aggiornamenti di Sentinel" hint="Decide quando i PC di questa azienda ricevono una versione nuova.">
            <select value={updateChannel} onChange={(e) => setUpdateChannel(e.target.value as 'auto' | 'provate' | 'bloccati')}>
              <option value="auto">Automatici: ogni versione nuova</option>
              <option value="provate">Solo versioni provate: dopo la tua conferma</option>
              <option value="bloccati">Bloccati: resta alla versione di oggi</option>
            </select>
          </Field>
          <label style={{ display: 'block', marginBottom: 8 }}><input type="checkbox" checked={modules.guard} onChange={(e) => setModules({ ...modules, guard: e.target.checked })} /> Monitoraggio continuo dei PC</label>
          <label style={{ display: 'block', marginBottom: 8 }}><input type="checkbox" checked={modules.assessment} onChange={(e) => setModules({ ...modules, assessment: e.target.checked })} /> Verifica completa ogni giorno</label>
          <label style={{ display: 'block', marginBottom: 8 }}><input type="checkbox" checked={modules.siem} disabled={!site.siemApprovedAt || licenseKind === 'trial'} onChange={(e) => setModules({ ...modules, siem: e.target.checked })} /> Riepiloghi SIEM nel CRM</label>
          <div className="muted small" style={{ marginBottom: 12 }}>{site.siemApprovedAt ? 'Documenti verificati. Accendi i riepiloghi dopo aver preparato i PC.' : 'Per accendere i riepiloghi, verifica i documenti nella sezione Registri di sicurezza.'} Gli agenti sui PC si gestiscono a parte.</div>
          <Field label="Chiavette USB" hint="Vale per tutti i PC con Guard. Un PC che ne ha bisogno (per esempio la firma digitale) può avere un'eccezione dalla sua scheda.">
            <select value={modules.usb ?? 'spento'} onChange={(e) => setModules({ ...modules, usb: e.target.value as GuardUsbMode })}>
              {(Object.keys(USB_MODE) as GuardUsbMode[]).map((m) => <option key={m} value={m}>{USB_MODE[m].long}</option>)}
            </select>
          </Field>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <Field label="Valido fino al" hint="La prova gratuita parte dal primo PC. Per una licenza attiva scegli la data concordata."><input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} /></Field>
            <Field label="Dispositivi della licenza" hint="Conta PC e server. Senza un numero, non si possono installare nuovi dispositivi."><input type="number" min="1" max="100000" value={limit} onChange={(e) => setLimit(e.target.value)} /></Field>
            <Field label="PC previsti per il SIEM" hint="Conta i PC e i server che dovranno inviare i registri. Lascia vuoto finché l'elenco non è approvato."><input type="number" min="1" max="100000" value={siemExpected} onChange={(e) => setSiemExpected(e.target.value)} /></Field>
          </div>
          {licenseKind === 'paid' && <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {[1, 2, 3].map((years) => <button key={years} type="button" className="btn small" onClick={() => setYears(years)}>{years} {years === 1 ? 'anno' : 'anni'}</button>)}
          </div>}
          <button type="button" className="btn dark" disabled={busy || (licenseKind === 'trial' && modules.siem) || (licenseKind === 'paid' && site.licenseKind === 'trial' && !expiresOn) || (!!limit && (!Number.isInteger(Number(limit)) || Number(limit) < 1)) || (!!siemExpected && (!Number.isInteger(Number(siemExpected)) || Number(siemExpected) < 1))} onClick={() => void save()}>{busy ? 'Salvataggio…' : 'Salva i servizi'}</button>
        </>
      ) : (
        <div className="small">
          {site.licenseKind === 'trial' ? 'Prova gratuita' : 'Licenza attiva'} · Aggiornamenti {site.updateChannel === 'auto' ? 'automatici' : site.updateChannel === 'provate' ? 'solo versioni provate' : 'bloccati'} · {site.modules.guard ? 'Controllo continuo attivo' : 'Controllo continuo spento'} · {site.modules.assessment ? 'Controllo completo attivo' : 'Controllo completo spento'} · {site.modules.siem ? 'Riepiloghi SIEM attivi' : 'Riepiloghi SIEM spenti'}
          {' · '}{USB_MODE[site.modules.usb ?? 'spento'].short}
          {' · '}{site.licenseKind === 'trial' && !site.trialStartedAt ? `${site.trialDays ?? 14} giorni dal primo PC` : site.licenseExpiresOn ? `fino al ${site.licenseExpiresOn.split('-').reverse().join('/')}` : 'senza scadenza'}
          {site.deviceLimit ? ` · ${site.devices}/${site.deviceLimit} dispositivi della licenza` : ' · posti della licenza da impostare prima delle installazioni'}
          {site.siemExpectedAgents ? ` · ${site.siemExpectedAgents} PC previsti per il SIEM` : ''}
        </div>
      )}
    </div>
  );
}

function AnalysisBox({ analysis, ai, busy, hasDevices, onAnalyze }: { analysis: GuardAnalysisDto | null; ai: boolean; busy: boolean; hasDevices: boolean; onAnalyze: () => void }) {
  const button = (
    <button
      type="button"
      className="btn"
      disabled={!ai || busy || !hasDevices}
      title={!ai ? 'L’analisi automatica non è ancora attiva sul server' : 'Legge lo stato di tutti i PC e scrive cosa fare prima'}
      onClick={onAnalyze}
    >
      <Icon name="star" size={16} /> {busy ? 'Analisi in corso…' : analysis ? 'Aggiorna analisi' : 'Analizza con AI'}
    </button>
  );
  if (!analysis) {
    return (
      <div className="guard-ai empty-ai">
        <span className="muted small grow">
          {ai ? 'Scrive per il titolare com’è la situazione dei PC e cosa fare prima.' : 'Analisi automatica non ancora attiva.'}
        </span>
        {button}
      </div>
    );
  }
  return (
    <div className="guard-ai">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Pill tone={RISK_TONE[analysis.risk]}>Rischio {analysis.risk}</Pill>
        <b className="grow">{analysis.headline}</b>
        {button}
      </div>
      <p style={{ margin: '10px 0' }}>{analysis.ownerSummary}</p>
      {analysis.priorities.length > 0 && (
        <ol className="guard-prio">
          {analysis.priorities.map((p, i) => (
            <li key={i}>
              <b>{p.title}</b>
              <div className="small">{p.why}</div>
              <div className="small muted">{p.action}</div>
            </li>
          ))}
        </ol>
      )}
      <div className="muted small">Analisi del {dateTime(analysis.generatedAt)}</div>
    </div>
  );
}

// Chiavette USB (27/10/2026): scelta per azienda, applicata da Guard su ogni PC.
const USB_MODE: Record<GuardUsbMode, { short: string; long: string }> = {
  spento: { short: 'Chiavette libere', long: 'Libere (nessuna regola)' },
  solo_cifrate: { short: 'Chiavette: si scrive solo su quelle cifrate', long: 'Si scrive solo su chiavette cifrate dall\'azienda (le altre solo in lettura)' },
  bloccate: { short: 'Chiavette bloccate', long: 'Bloccate: niente chiavette né dischi esterni' },
};

function usbCell(d: GuardDeviceDto, mode: GuardUsbMode) {
  if (d.usbException) return <Pill tone="soft">Eccezione</Pill>;
  const p = d.state?.usbPolicy;
  if (mode === 'spento' && (!p || p.mode === 'spento')) return <span className="muted small">libere</span>;
  if (!p) return <Pill tone="warn">In attesa</Pill>;
  if (p.mode !== mode) return <Pill tone="warn">In arrivo</Pill>;
  return p.applied ? <Pill tone="good">{mode === 'bloccate' ? 'Bloccate' : 'Solo cifrate'}</Pill> : <Pill tone="bad">Non applicate</Pill>;
}

/** Guard gestisce le chiavette dalla 0.1.3: senza regola per l'azienda il PC non manda niente e le chiavette sono libere. */
function guardHandlesUsb(version: string | null | undefined): boolean {
  const [a = 0, b = 0, c = 0] = (version ?? '').split('.').map((n) => Number.parseInt(n, 10) || 0);
  return a > 0 || b > 1 || (b === 1 && c >= 3);
}

function usbDetail(d: GuardDeviceDto, mode: GuardUsbMode): string {
  if (d.usbException) return `eccezione: ${d.usbException}`;
  const p = d.state?.usbPolicy;
  if (p) return `${USB_MODE[p.mode].short}${p.applied ? '' : ' (non applicate)'}${p.note ? ` · ${p.note}` : ''}`;
  if (!guardHandlesUsb(d.agentVersion)) return 'questa versione di Guard non gestisce le chiavette';
  return mode === 'spento' ? USB_MODE.spento.long : 'in attesa: il PC non ha ancora applicato la regola dell\'azienda';
}

function DeviceRow({ d, usb, onOpen }: { d: GuardDeviceDto; usb: GuardUsbMode; onOpen: () => void }) {
  const st = d.state;
  const avOn = st ? st.antivirus.some((a) => a.enabled) || !!st.defender?.realTime : null;
  const avName = st?.antivirus.find((a) => a.enabled)?.name ?? st?.antivirus[0]?.name ?? (st?.defender ? 'Defender' : '—');
  const avStale = st ? st.antivirus.some((a) => a.enabled && !a.upToDate) : false;
  const fwOff = st ? (['domain', 'private', 'public'] as const).some((p) => st.firewall[p] === false) : false;
  return (
    <tr className="clickable" onClick={onOpen}>
      <td>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <StatusDot on={d.online} />
          <span>
            <b>{d.label || d.name}</b>
            <div className="muted small">{d.label ? `${d.name} · ` : ''}{d.location ? `${d.location} · ` : ''}{d.online ? 'in linea' : d.lastSeen ? `visto ${when(d.lastSeen)}` : 'nessun dato ricevuto'}</div>
          </span>
        </span>
      </td>
      <td>{st ? <Pill tone={!avOn ? 'bad' : avStale ? 'warn' : 'good'}>{!avOn ? 'Spento' : avStale ? 'Non aggiornato' : avName}</Pill> : '—'}</td>
      <td>{st ? <Pill tone={fwOff ? 'bad' : 'good'}>{fwOff ? 'Spento' : 'Attivo'}</Pill> : '—'}</td>
      <td>{st ? <Pill tone={st.bitlocker === 'on' ? 'good' : st.bitlocker === 'off' ? 'warn' : 'soft'}>{st.bitlocker === 'on' ? 'Attivo' : st.bitlocker === 'off' ? 'Spento' : '?'}</Pill> : '—'}</td>
      <td className="small">{st?.lastUpdateDays == null ? '—' : st.lastUpdateDays <= 35 ? `${st.lastUpdateDays} g fa` : <span style={{ color: 'var(--bad)' }}>{st.lastUpdateDays} g fa</span>}</td>
      <td className="small">{d.report ? `${d.report.nonCompliant} azioni consigliate · ${when(d.report.finishedAt)}` : <span className="muted">in attesa</span>}</td>
      <td title={d.usbException ?? d.state?.usbPolicy?.note ?? undefined}>{usbCell(d, usb)}</td>
      <td className="num">{d.openAlerts > 0 ? <Pill tone="bad">{d.openAlerts}</Pill> : <span className="muted">0</span>}</td>
    </tr>
  );
}

function EventRow({ e, onAck }: { e: GuardEventDto; onAck?: () => void }) {
  return (
    <div className="ag-row" style={{ cursor: 'default', alignItems: 'flex-start' }}>
      <Pill tone={SEV_TONE[e.severity]}>{SEV_LABEL[e.severity]}</Pill>
      <span className="ag-title">
        <b>{e.title}</b>
        <div className="muted small">
          {e.deviceName} · {dateTime(e.occurredAt)}
          {e.ackAt ? ` · vista${e.ackByName ? ' da ' + e.ackByName : ''}` : ''}
        </div>
        {e.detail && <div className="guard-detail small">{e.detail}</div>}
      </span>
      {onAck && (
        <button type="button" className="btn small" title="L’hai guardato: sparisce dagli avvisi" onClick={onAck}>
          Visto
        </button>
      )}
    </div>
  );
}

function DeviceModal({ d, usb, events, onClose, onDelete, onUsbException, onLabel }: { d: GuardDeviceDto; usb: GuardUsbMode; events: GuardEventDto[]; onClose: () => void; onDelete: () => void;
  onUsbException: (note: string | null) => Promise<void>; onLabel: (label: string, location: string) => Promise<void> }) {
  const st = d.state;
  const [usbNote, setUsbNote] = useState(d.usbException ?? '');
  const [label, setLabel] = useState(d.label ?? '');
  const [location, setLocation] = useState(d.location ?? '');
  return (
    <Modal
      title={d.label || d.name}
      onClose={onClose}
      footer={
        <button type="button" className="btn ghost" title="Il PC non c’è più o è stato cambiato" onClick={onDelete}>
          <Icon name="trash" size={16} /> Togli dall’elenco
        </button>
      }
    >
      <div className="muted small" style={{ marginBottom: 12 }}>
        {d.label ? `${d.name} · ` : ''}{d.os ?? 'Windows'} · Tecnoside Guard {d.agentVersion ?? '?'} · {d.online ? 'in linea' : d.lastSeen ? `visto ${when(d.lastSeen)}` : 'nessun dato ricevuto'}
        {st?.user ? ` · utente ${st.user}` : ''}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <Field label="Nome del PC" hint="Per riconoscerlo in assistenza">
            <input value={label} maxLength={120} placeholder="es. PC reception" onChange={(e) => setLabel(e.target.value)} />
          </Field>
        </div>
        <div style={{ flex: 1 }}>
          <Field label="Dove si trova" hint="Sede, stanza o persona">
            <input value={location} maxLength={160} placeholder="es. Torre del Greco, accettazione" onChange={(e) => setLocation(e.target.value)} />
          </Field>
        </div>
      </div>
      <button type="button" className="btn small dark" style={{ marginBottom: 14 }} disabled={label === (d.label ?? '') && location === (d.location ?? '')} onClick={() => void onLabel(label, location)}>Salva nome e posizione</button>
      {st && (
        <dl className="guard-kv">
          <dt>Antivirus</dt>
          <dd>{st.antivirus.length ? st.antivirus.map((a) => `${a.name}${a.enabled ? '' : ' (spento)'}${a.upToDate ? '' : ' (non aggiornato)'}`).join(', ') : 'nessuno registrato'}</dd>
          {st.defender && (
            <>
              <dt>Defender</dt>
              <dd>
                {st.defender.mode ?? '?'} · protezione in tempo reale {st.defender.realTime ? 'attiva' : 'spenta'}
                {st.defender.signatureAgeDays != null ? ` · elenco dei virus aggiornato ${st.defender.signatureAgeDays} g fa` : ''}
                {st.defender.tamperProtected === false ? ' · protezione dalle manomissioni spenta' : ''}
              </dd>
            </>
          )}
          <dt>Firewall</dt>
          <dd>
            dominio {st.firewall.domain === false ? 'spento' : 'attivo'} · privata {st.firewall.private === false ? 'spento' : 'attivo'} · pubblica{' '}
            {st.firewall.public === false ? 'spento' : 'attivo'}
          </dd>
          <dt>Aggiornamenti</dt>
          <dd>
            {st.lastUpdateDays == null ? '?' : `ultimo ${st.lastUpdateDays} giorni fa`}
            {st.pendingReboot ? ' · riavvio in sospeso' : ''}
          </dd>
          <dt>Chiavette USB</dt>
          <dd>{usbDetail(d, usb)}</dd>
        </dl>
      )}
      {(usb !== 'spento' || d.usbException) && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, margin: '8px 0 12px' }}>
          <b className="small">Eccezione per questo PC</b>
          <div className="muted small" style={{ margin: '4px 0 6px' }}>Su questo PC le chiavette restano libere. Scrivi il motivo: resta come prova per la NIS2.</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={{ flex: 1 }} value={usbNote} maxLength={300} placeholder="es. chiavetta della firma digitale dell'amministrazione" onChange={(e) => setUsbNote(e.target.value)} />
            <button type="button" className="btn small dark" disabled={usbNote.trim().length < 5} onClick={() => void onUsbException(usbNote.trim())}>Salva l'eccezione</button>
            {d.usbException && <button type="button" className="btn small ghost" onClick={() => void onUsbException(null)}>Togli</button>}
          </div>
        </div>
      )}
      <h3 className="guard-h">Azioni consigliate {d.report ? `(verifica del ${dateTime(d.report.finishedAt)})` : ''}</h3>
      {!d.report ? (
        <div className="muted small">La verifica completa arriva entro 24 ore dall'installazione.</div>
      ) : d.report.issues.length === 0 ? (
        <div className="muted small">Nessuna azione consigliata.</div>
      ) : (
        <div className="ag-list">
          {d.report.issues.map((x, i) => (
            <div key={i} className="ag-row" style={{ cursor: 'default', alignItems: 'flex-start' }}>
              <Pill tone={SEV_TONE[x.severity]}>{SEV_LABEL[x.severity]}</Pill>
              <span className="ag-title">
                <b>{x.title}</b>
                {x.recommendation && <div className="muted small">{x.recommendation}</div>}
              </span>
            </div>
          ))}
        </div>
      )}
      <h3 className="guard-h">Ultime cose successe</h3>
      {events.length === 0 ? (
        <div className="muted small">Niente da mostrare.</div>
      ) : (
        <div className="ag-list">
          {events.slice(0, 20).map((e) => (
            <EventRow key={e.id} e={e} />
          ))}
        </div>
      )}
    </Modal>
  );
}
